import { Injectable } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { ResponseStatus } from '../../common/Interfaces';
import { LoggingService } from './logging/logging.service';

@Injectable({
    providedIn: 'root'
})
export class GlobalErrorDialogService {
    private isDialogOpen = false;
    private currentDialogId = '';

    constructor(
        private confirmationService: ConfirmationService,
        private log: LoggingService
    ) {}

    showError(errorMessage: string, status: ResponseStatus = ResponseStatus.Failed): void {
        const dialogId = `error-${Date.now()}-${Math.random()}`;
        
        // Prevent duplicate dialogs
        if (this.isDialogOpen) {
            this.log.verbose({
                type: 'GlobalErrorDialogService:showError',
                message: 'Dialog already open, skipping',
                errorMessage,
                currentDialogId: this.currentDialogId,
                newDialogId: dialogId
            });
            return;
        }

        this.isDialogOpen = true;
        this.currentDialogId = dialogId;

        let headerText = 'ERROR';
        let icon = 'pi pi-times-circle';
        let acceptButtonClass = 'p-button-danger p-button-text';

        switch (status) {
            case ResponseStatus.Success:
                headerText = 'SUCCESS';
                icon = 'pi pi-check-circle';
                acceptButtonClass = 'p-button-success p-button-text';
                break;
            case ResponseStatus.Warning:
                headerText = 'WARNING';
                icon = 'pi pi-exclamation-triangle';
                acceptButtonClass = 'p-button-warning p-button-text';
                break;
            default:
                headerText = 'ERROR';
                icon = 'pi pi-times-circle';
                acceptButtonClass = 'p-button-danger p-button-text';
                break;
        }

        this.log.verbose({
            type: 'GlobalErrorDialogService:showError',
            message: 'Showing error dialog',
            errorMessage,
            headerText,
            status,
            dialogId
        });

        // Clear any existing confirmation dialogs first
        try {
            this.confirmationService.close();
        } catch (e) {
            // Ignore errors if no dialog is open
        }

        this.confirmationService.confirm({
            key: 'global-error-dialog',
            message: errorMessage,
            header: headerText,
            icon: icon,
            acceptLabel: 'OK',
            rejectVisible: false,
            acceptButtonStyleClass: acceptButtonClass,
            accept: () => {
                this.closeDialog(dialogId, 'accept');
            },
            reject: () => {
                this.closeDialog(dialogId, 'reject');
            }
        });

        setTimeout(() => {
            if (this.isDialogOpen && this.currentDialogId === dialogId) {
                this.closeDialog(dialogId, 'timeout');
            }
        }, 10000);
    }

    private closeDialog(dialogId: string, reason: string): void {
        if (this.currentDialogId === dialogId) {
            this.isDialogOpen = false;
            this.currentDialogId = '';
            this.log.verbose({
                type: 'GlobalErrorDialogService:closeDialog',
                message: 'Dialog closed',
                dialogId,
                reason
            });
        } else {
            this.log.verbose({
                type: 'GlobalErrorDialogService:closeDialog',
                message: 'Dialog ID mismatch, not closing',
                expectedDialogId: this.currentDialogId,
                receivedDialogId: dialogId,
                reason
            });
        }
    }

    isOpen(): boolean {
        return this.isDialogOpen;
    }
}